import { appLocalStorage, createAppAsyncThunk } from '@/warehouse/helpers';
import { isServerResponseError } from '@/core/utils/errorHandling';
import { stocksApi } from '../stocks.api';
/**
 * Before Layout has started it's loading we need to know what crossDockId and locationId should be active at the moment.
 *
 * Id from url param should have the most priority, since the link can be copied and pasted in browser.
 * localStorage value should be taken next, if no param exists in url.
 * Then, if localStorage contains no value by given key, or the value is outside of available for the user crossDocks or
 * locations range, the first value from available list should be used.
 *
 * Handling situation when, for some reason, stored stockLocationId is accidentally not existed in StockLocationLookup.
 * It may happen if, for example, current user has lost access to the stock location with stored id. Or the value in
 * localStorage had not been existed before and equals to `undefined`.
 * It this case the first stockLocation (or crossdock) in the list is set as current stock location (or current crossdock).
 */
const initiateCrossdocksAndLocations = createAppAsyncThunk('stocks/initiateCrossdocksAndLocations', async (params, { dispatch, rejectWithValue, fulfillWithValue }) => {
    try {
        let result;
        // TODO: to delete later, this is just a cleaning call.
        appLocalStorage.removeItem('crossdockingId');
        const task1 = dispatch(stocksApi.endpoints.getCrossdockLookup.initiate()).unwrap();
        const task2 = dispatch(stocksApi.endpoints.getStockLocations.initiate()).unwrap();
        // TODO: rework here . Instead of two parallel request - use one useData request - same security data.
        // Then after we remove userData from localStorage, this endpoint will be available everywhere.
        const [crossdockLookup, stockLocationLookup] = await Promise.all([task1, task2]);
        if (crossdockLookup?.length) {
            const paramCrossDockId = Number(params.crossDockId);
            if (paramCrossDockId && crossdockLookup.find((item) => item.id === paramCrossDockId)) {
                appLocalStorage.setItem('crossDockId', paramCrossDockId.toString());
            }
            else {
                // At this step, crossDockId from url points the crossDock user doesn't have access to.
                if (paramCrossDockId) {
                    result = { unaccessibleCrossDockId: paramCrossDockId };
                }
                const storedCrossDockId = appLocalStorage.getItem('crossDockId');
                const crossdockId = Number(storedCrossDockId);
                if (!crossdockId || !crossdockLookup.find((item) => item.id === crossdockId)) {
                    appLocalStorage.setItem('crossDockId', crossdockLookup[0].id.toString());
                    // Do not use reconnect here. No connection has been started on this stage.
                    // signalrHubConnector_New(SignalrConnectionHub_New.Crossdocking).reconnect();
                }
            }
        }
        else {
            // This case means the user has no access to Crossdocking group sections.
            appLocalStorage.removeItem('crossDockId');
        }
        if (stockLocationLookup?.length) {
            const paramLocationId = Number(params.locationId);
            if (paramLocationId && stockLocationLookup.find((item) => item.id === paramLocationId)) {
                appLocalStorage.setItem('stockLocationId', paramLocationId.toString());
            }
            else {
                // At this step, locationId from url points the location user doesn't have access to.
                if (paramLocationId) {
                    result = { unaccessibleLocationId: paramLocationId };
                }
                const storedStockLocationId = appLocalStorage.getItem('stockLocationId');
                const stockLocationId = Number(storedStockLocationId);
                if (!stockLocationId || !stockLocationLookup.find((item) => item.id === stockLocationId)) {
                    appLocalStorage.setItem('stockLocationId', stockLocationLookup[0].id.toString());
                }
            }
        }
        else {
            // This case means the user has no access to StockLocations group sections.
            appLocalStorage.removeItem('stockLocationId');
        }
        return fulfillWithValue(result);
    }
    catch (error) {
        console.error(`Crossdock or location lookups request error:`, error);
        if (isServerResponseError(error)) {
            if (error.status === 401)
                return rejectWithValue({ errorMessage: 'Get user data unauthorized server response' });
            return rejectWithValue({ errorMessage: `Server unexpected error with status ${error.status}` });
        }
        if (error instanceof Error) {
            return rejectWithValue({ errorMessage: `Cannot load crossdock or location lookups: ${error.message}` });
        }
        return rejectWithValue({ errorMessage: 'Server unexpected error' });
    }
});
export { initiateCrossdocksAndLocations };
